import React, { useCallback, useEffect, useState } from "react";
import { UpOutlined } from "@ant-design/icons";
import { FloatButton } from "antd";

const DEFAULT_VISIBILITY_HEIGHT = 400;

interface BackTopButtonProps {
  // Scroll distance (px) before the button appears; if omitted, it's derived from how much the page can actually scroll.
  visibilityHeight?: number;
}

// Some pages scroll the window, others scroll an inner container (e.g. a fixed-height table body).
const findScrollTarget = (): HTMLElement | Window => {
  const root = document.querySelector<HTMLElement>(".site-layout-content") ?? document.body;
  let best: HTMLElement | null = null;
  let bestDelta = 0;

  root.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const delta = el.scrollHeight - el.clientHeight;
    if (delta <= 10 || delta <= bestDelta) return;
    if (!/(auto|scroll)/.test(window.getComputedStyle(el).overflowY)) return;
    bestDelta = delta;
    best = el;
  });

  return best ?? window;
};

const getMaxScroll = (target: HTMLElement | Window): number => {
  if (target === window) {
    return document.documentElement.scrollHeight - window.innerHeight;
  }
  const el = target as HTMLElement;
  return el.scrollHeight - el.clientHeight;
};

const BackTopButton: React.FC<BackTopButtonProps> = ({ visibilityHeight }) => {
  const [target, setTarget] = useState<HTMLElement | Window>(() => window);
  const [autoThreshold, setAutoThreshold] = useState(DEFAULT_VISIBILITY_HEIGHT);

  const recalc = useCallback(() => {
    const nextTarget = findScrollTarget();
    const maxScroll = getMaxScroll(nextTarget);
    setTarget(nextTarget);
    // Short pages: show once a third of the available scroll is used, capped at the default.
    setAutoThreshold(maxScroll > 0 ? Math.min(DEFAULT_VISIBILITY_HEIGHT, maxScroll * 0.3) : DEFAULT_VISIBILITY_HEIGHT);
  }, []);

  useEffect(() => {
    if (visibilityHeight !== undefined) return;

    recalc();
    const root = document.querySelector<HTMLElement>(".site-layout-content") ?? document.body;

    window.addEventListener('resize', recalc);
    const resizeObserver = new ResizeObserver(recalc);
    resizeObserver.observe(root);

    let mutationTimeout: ReturnType<typeof setTimeout>;
    const mutationObserver = new MutationObserver(() => {
      clearTimeout(mutationTimeout);
      mutationTimeout = setTimeout(recalc, 200);
    });
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', recalc);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      clearTimeout(mutationTimeout);
    };
  }, [recalc, visibilityHeight]);

  const threshold = visibilityHeight ?? autoThreshold;

  return (
    <FloatButton.BackTop
      key={`${threshold}-${target === window ? 'window' : 'element'}`}
      target={() => target}
      style={{ right: 30, bottom: 70 }}
      visibilityHeight={threshold}
    >
      <UpOutlined className="back-top-btn" />
    </FloatButton.BackTop>
  );
};

export default BackTopButton;